import React, { useState, useEffect, useRef } from "react";
import { Input, Select, Button,message,Radio,Modal } from "antd";
import {DeleteOutlined,PlusCircleOutlined,PlayCircleOutlined,CopyOutlined} from "@ant-design/icons";

import {buildNodeTypeParamsForm} from "./func/buildNodeTypeParamsForm";
import {buildSelectedElementData} from "./func/buildSelectedElementData";
import {queryRelateElement} from "./func/queryRelateElement";
import { rebuildFormula } from "@/config/func";

import { unitMap,dataTypeMap } from "@/constants/dataMap";

interface ParamsEditProps
{
    selectedElementData:any;
    dataSource:any;
    globalData:any;
    flowChartType:string;
    selectTaskData:any;
    selectDesignData:any;
    closeParamsDrawer:()=>void;
    selectTreeItem:(node)=>void;
    upData:(data)=>void;
    runPartTask:(nodeKey)=>void;
}

const ParamsEdit: React.FC<ParamsEditProps> = 
({
    selectedElementData,
    dataSource,
    globalData,
    flowChartType,
    selectTaskData,
    selectDesignData,
    closeParamsDrawer,
    selectTreeItem,
    upData,
    runPartTask,
}) => 
{
    const [messageApi, contextHolder] = message.useMessage();

    const projectId = 138;  //项目ID
    const flowId = 532; //流程ID
    const [initializeVariable,setInitializeVariable] = useState(false);     //初始化完成表示，若为true代表初始化完成，可以渲染前端组件
    const [paramDatas,setParamDatas] = useState(null);  //参数数据，该格式用于构建该页面
    const [relateElements,setRelateElements] = useState({fromNodes:[],toNodes:[],allNodes:[],inLinks:[],outLinks:[],allLinks:[]});  //关联元件/流股/分组数据
    const [copyMaterialVariable,setCopyMaterialVariable] = useState(false);  //控制打开粘贴组分弹窗
    const [copyMaterialsDataTxt,setCopyMaterialsDataTxt] = useState("");  //粘贴组分的文本

    //切换所选元件/流股/分组时触发
    useEffect(() => 
    {
        setParamDatas(null);
        if(selectedElementData)
        {
            initializeFunc();
        }
    }, [selectedElementData]);

    //初始化函数
    const initializeFunc = async() =>
    {
        if(selectedElementData.elementType==="node")
        {
            //元件
            let nodeTypeParamsForm = await buildNodeTypeParamsForm();
            let secondaryTypeName = selectedElementData.elementSecondaryTypeName;
            let originalParamDatas = nodeTypeParamsForm[secondaryTypeName];
            let jsonParamDatas = selectedElementData.paramsData;

            //构建参数分类
            let paramsDataClasses = {"规定":[],"描述":[]};
            for (let engName in originalParamDatas)
            {
                let originalParamData = originalParamDatas[engName];

                let paramName = originalParamData.p_name;
                let paramClass = originalParamData.p_class ?? "规定";
                let paramUnit = originalParamData.p_unit ?? null;
                let paramEnum = originalParamData.enum ?? null;
                let paramUnitField = originalParamData.p_unitField ?? null;

                let jsonParamData = jsonParamDatas[paramName];
                let paramShow = jsonParamData.show ?? originalParamData.p_show;
                let paramValue = jsonParamData.value ?? null;
                let paramUnitValue = jsonParamData.unit ?? null;
                let paramChildren = jsonParamData.p_children ?? null;

                //单位类型
                if(originalParamData.p_type)
                {
                    if(unitMap[originalParamData.p_type])
                    {
                        paramUnit = unitMap[originalParamData.p_type];
                    }
                }

                //修改单位枚举格式
                if(paramUnit)
                {
                    paramUnit = paramUnit.map(item => ({value:item,label:item}));
                }

                //特殊处理
                if(selectedElementData.elementNodeType==="DistilStrict" && (paramName==="进料流股位置"||paramName==="出料流股位置"||paramName==="塔板效率"))
                {
                    paramClass = paramName;
                }
                if(selectedElementData.elementNodeType==="Splitter" && (paramName==="输出流量"))
                {
                    paramClass = paramName;
                }
                if(selectedElementData.elementNodeType==="MultiStreamExchanger" && selectedElementData.elementSecondaryTypeName==="换热器4" && (paramName==="冷流股出口温度"||paramName==="冷流股出口压力"||paramName==="热流股出口温度"||paramName==="热流股出口压力"||paramName==="出入流股关系"))
                {
                    paramClass = paramName;
                }
                if(selectedElementData.elementNodeType==="DistilStrict" && paramName==="内部类型")
                {
                    paramShow = false;
                }
                if(selectedElementData.elementNodeType==="MultiStreamExchanger" && selectedElementData.elementSecondaryTypeName==="换热器4" && paramName==="流股属性")
                {
                    paramShow = false;
                }

                let paramData = 
                {
                    name:paramName,
                    engName:engName,
                    show:paramShow,
                    class:paramClass,
                    value:paramValue,
                    unitValue:paramUnitValue,
                    unit:paramUnit,
                    unitField:paramUnitField,
                    enum:paramEnum,
                    children:paramChildren,
                }

                if(paramsDataClasses[paramClass])
                {
                    paramsDataClasses[paramClass].push(paramData);
                }
                else
                {
                    paramsDataClasses[paramClass] = [];
                    paramsDataClasses[paramClass].push(paramData);
                }
            }

            //描述特殊处理
            let descriptionData = 
            {
                name:"描述",
                engName:"description",
                show:true,
                class:"描述",
                value:selectedElementData.basicData["描述"].value,
                unitValue:null,
                unit:null,
                unitField:null,
                enum:null,
                children:null,
            };
            paramsDataClasses["描述"].push(descriptionData);

            //将paramsDataClasses改为数组并排序
            let newParamDatas = [];
            for (let className in paramsDataClasses)
            {
                //操作参数、几何参数、设计工况、设计裕量、壁面条件 这几个部分不要了，直接过滤不显示
                if(["操作参数","几何参数","设计工况","设计裕量","壁面条件"].includes(className))
                {
                    continue;
                }
                let classData = 
                {
                    className:className,
                    params:paramsDataClasses[className],
                };
                newParamDatas.push(classData);
            }
            //排序
            if(selectedElementData.elementNodeType==="DistilStrict")
            {
                let classOrder = 
                [
                    "工艺名称",
                    "规定",
                    "收敛选项",
                    "塔板参数",
                    "填料参数",
                    "塔板效率",
                    "进料流股位置",
                    "出料流股位置",
                    //"操作参数",
                    //"几何参数",
                    "水力参数",
                    //"设计工况",
                    //"设计裕量",
                    //"壁面条件",
                    "描述",
                ];
                newParamDatas.sort((a,b) => (classOrder.indexOf(a.className) + 1 || classOrder.length + 1) - (classOrder.indexOf(b.className) + 1 || classOrder.length + 1));
            }
            else if(selectedElementData.elementNodeType==="Splitter")
            {
                let classOrder = 
                [
                    "规定",
                    "输出流量",
                    "描述",
                ];
                newParamDatas.sort((a,b) => (classOrder.indexOf(a.className) + 1 || classOrder.length + 1) - (classOrder.indexOf(b.className) + 1 || classOrder.length + 1));
            }
            else if(selectedElementData.elementNodeType==="MultiStreamExchanger" && selectedElementData.elementSecondaryTypeName==="换热器4")
            {
                let classOrder = 
                [
                    "规定",
                    "出入流股关系",
                    "冷流股出口温度",
                    "冷流股出口压力",
                    "热流股出口温度",
                    "热流股出口压力",
                    "描述",
                ];
                newParamDatas.sort((a,b) => (classOrder.indexOf(a.className) + 1 || classOrder.length + 1) - (classOrder.indexOf(b.className) + 1 || classOrder.length + 1));
            }
            else
            {
                let classOrder = 
                [
                    "规定",
                    "描述",
                ];
                newParamDatas.sort((a,b) => (classOrder.indexOf(a.className) + 1 || classOrder.length + 1) - (classOrder.indexOf(b.className) + 1 || classOrder.length + 1));
            }

            //查询关联流股
            let newRelateElements = await queryRelateElement(selectedElementData,dataSource);
            setParamDatas(newParamDatas);
            setRelateElements(newRelateElements);
        }
        else if(selectedElementData.elementType==="link")
        {
            //流股
            let nodeTypeParamsForm = await buildNodeTypeParamsForm();

            let secondaryTypeName = "link";
            let originalParamDatas = nodeTypeParamsForm[secondaryTypeName];
            let jsonParamDatas = selectedElementData.paramsData;
            let materialsDatas = selectedElementData.materialsData;

            //构建参数分类
            let paramsDataClasses = {"规定":[],"描述":[],"组分":[]};
            for (let engName in originalParamDatas)
            {
                let originalParamData = originalParamDatas[engName];

                let paramName = originalParamData.p_name;
                let paramClass = originalParamData.p_class ?? "规定";
                let paramUnit = originalParamData.p_unit ?? null;
                let paramUnitField = originalParamData.p_unitField ?? null;
                let paramEnum = originalParamData.enum ?? null;
                let paramShow = originalParamData.p_show;

                let jsonParamData = jsonParamDatas[paramName];
                let paramValue = jsonParamData.value ?? null;
                let paramUnitValue = jsonParamData.unit ?? null;
                let paramChildren = jsonParamData.p_children ?? null;

                //单位类型
                if(originalParamData.p_type)
                {
                    if(unitMap[originalParamData.p_type])
                    {
                        paramUnit = unitMap[originalParamData.p_type];
                    }
                }

                //修改单位枚举格式
                if(paramUnit)
                {
                    paramUnit = paramUnit.map(item => ({value:item,label:item}));
                }

                //特殊处理
                if(paramName==="流股属性")
                {
                    paramShow = false;
                }

                let paramData = 
                {
                    name:paramName,
                    engName:engName,
                    show:paramShow,
                    class:paramClass,
                    value:paramValue,
                    unitValue:paramUnitValue,
                    unit:paramUnit,
                    unitField:paramUnitField,
                    enum:paramEnum,
                    children:paramChildren,
                }

                if(paramsDataClasses[paramClass])
                {
                    paramsDataClasses[paramClass].push(paramData);
                }
                else
                {
                    paramsDataClasses[paramClass] = [];
                    paramsDataClasses[paramClass].push(paramData);
                }
            }

            //描述特殊处理
            let descriptionData = 
            {
                name:"描述",
                engName:"description",
                show:true,
                class:"描述",
                value:selectedElementData.basicData["描述"].value,
                unitValue:null,
                unit:null,
                unitField:null,
                enum:null,
                children:null,
            };
            paramsDataClasses["描述"].push(descriptionData);

            //组分特殊处理
            for (let materialName in materialsDatas)
            {
                let materialsData = materialsDatas[materialName];
                let paramData = 
                {
                    name:materialsData.chineseName,
                    engName:materialName,
                    show:true,
                    class:"组分",
                    value:materialsData.molar_frac,
                    unitValue:null,
                    unit:null,
                    unitField:null,
                    enum:null,
                    children:null,
                };
                paramsDataClasses["组分"].push(paramData);
            }

            //将paramsDataClasses改为数组并排序
            let newParamDatas = [];
            for (let className in paramsDataClasses)
            {
                let classData = 
                {
                    className:className,
                    params:paramsDataClasses[className],
                };
                newParamDatas.push(classData);
            }
            //排序
            let classOrder = 
            [
                "规定",
                "描述",
                "组分",
            ];
            newParamDatas.sort((a,b) => (classOrder.indexOf(a.className) + 1 || classOrder.length + 1) - (classOrder.indexOf(b.className) + 1 || classOrder.length + 1));
            
            //查询关联元件
            let newRelateElements = await queryRelateElement(selectedElementData,dataSource);

            setParamDatas(newParamDatas);
            setRelateElements(newRelateElements);
        }
        else if(selectedElementData.elementType==="group")
        {
            //分组

            //构建参数分类
            let paramsDataClasses = {"描述":[]};

            //描述特殊处理
            let descriptionData = 
            {
                name:"描述",
                engName:"description",
                show:true,
                class:"描述",
                value:selectedElementData.basicData["描述"].value,
                unitValue:null,
                unit:null,
                unitField:null,
                enum:null,
                children:null,
            };
            paramsDataClasses["描述"].push(descriptionData);

            //将paramsDataClasses改为数组并排序
            let newParamDatas = [];
            for (let className in paramsDataClasses)
            {
                let classData = 
                {
                    className:className,
                    params:paramsDataClasses[className],
                };
                newParamDatas.push(classData);
            }
            //排序
            let classOrder = 
            [
                "描述",
            ];
            newParamDatas.sort((a,b) => (classOrder.indexOf(a.className) + 1 || classOrder.length + 1) - (classOrder.indexOf(b.className) + 1 || classOrder.length + 1));

            //查询关联元件/流股
            let newRelateElements = await queryRelateElement(selectedElementData,dataSource);

            setParamDatas(newParamDatas);
            setRelateElements(newRelateElements);
        }

        //初始化完成
        setInitializeVariable(true);
    }

    //保存本地参数
    const saveLoaclParams = async() =>
    {
        if(!paramDatas)
        {
            messageApi.open
            ({
                type: "error",
                content: "数据有误，保存失败",
            });
            return false;
        }

        let paramValues = structuredClone(selectedElementData.paramsData);
        let basicParamValues = structuredClone(selectedElementData.basicData);
        let materialsParamValues = structuredClone(selectedElementData.materialsData);

        for(let i=0;i<paramDatas.length;i++)
        {
            let classParams = paramDatas[i].params;
            let className = paramDatas[i].className;

            if(className==="描述")
            {
                //描述特殊处理
                if(classParams[0])
                {
                    let paramName = classParams[0].name;
                    let paramValue = classParams[0].value;

                    basicParamValues[paramName].value = paramValue;
                }
            }
            else if(className==="组分")
            {
                //组分特殊处理
                for(let j=0;j<classParams.length;j++)
                {
                    let param = classParams[j];
                    let paramEngName = param.engName;
                    let paramValue = param.value;

                    //value转换类型
                    if(paramValue)
                    {
                        paramValue = Number(paramValue);
                    }
                    materialsParamValues[paramEngName].molar_frac = paramValue;
                }
            }
            else
            {
                for(let j=0;j<classParams.length;j++)
                {
                    let param = classParams[j];
                    let paramName = param.name;

                    let paramValue = param.value;
                    let paramUnitValue = param.unitValue;
                    let paramShow= param.show;
                    let paramChildren= param.children;

                    //value转换类型
                    if(dataTypeMap[paramName] && paramValue)
                    {
                        if(dataTypeMap[paramName]==="number")
                        {
                            paramValue = Number(paramValue);
                        }
                        else if(dataTypeMap[paramName]==="string")
                        {
                            paramValue = String(paramValue);
                        }
                    }
                    
                    paramValues[paramName].show = paramShow;
                    if(paramValues[paramName].value!==null && paramValues[paramName].value!==undefined)
                    {
                        paramValues[paramName].value = paramValue;
                    }
                    if(paramValues[paramName].unit!==null && paramValues[paramName].unit!==undefined)
                    {
                        paramValues[paramName].unit = paramUnitValue;
                    }
                    if(paramValues[paramName].p_children!==null && paramValues[paramName].p_children!==undefined)
                    {
                        paramValues[paramName].p_children = paramChildren;
                    }
                }
            }
        }

        if(selectedElementData.elementType==="node")
        {
            //元件
            let saveData = 
            {
                type:selectedElementData.elementType,
                key:selectedElementData.elementID,
                basicData:basicParamValues,
                paramsData:paramValues
            };
            
            setParamDatas(null);

            upData(saveData);
        }
        else if(selectedElementData.elementType==="link")
        {
            //流股
            let saveData = 
            {
                type:selectedElementData.elementType,
                key:selectedElementData.elementID,
                basicData:basicParamValues,
                paramsData:paramValues,
                materialsData:materialsParamValues,
            };

            setParamDatas(null);

            upData(saveData);
        }
        else if(selectedElementData.elementType==="group")
        {
            //分组
            let saveData = 
            {
                type:selectedElementData.elementType,
                key:selectedElementData.elementID,
                basicData:basicParamValues,
            };
            
            setParamDatas(null);

            upData(saveData);
        }

        return true;
    }

    //组件修改参数值
    const upParamData = async(engName,updates,isSpec?) =>
    {
        setParamDatas((prev) =>prev.map((classData) => 
            ({
                ...classData,
                params:classData.params.map((param) =>
                    param.engName === engName ? { ...param,...updates} : param
                ),
            }))
        );

        //特殊处理
        if(isSpec)
        {
            upSpecParamData(engName,updates);
        }
    }

    //组件修改参数值时的特殊处理
    const upSpecParamData = async(engName,updates) =>
    {
        //内部类型
        if(engName==="internalType" && selectedElementData.elementNodeType==="DistilStrict")
        {
            if(updates.value==="塔板")
            {
                await upParamData("trayType",{show:true});
                await upParamData("N",{show:true});
                await upParamData("theta",{show:true});
                await upParamData("h_w",{show:true});
                await upParamData("tray_material",{show:true});
                await upParamData("overflow_type",{show:true});
                await upParamData("packingType",{show:false});
                await upParamData("packing_total_height",{show:false});
            }
            else if(updates.value==="填料")
            {
                await upParamData("trayType",{show:false});
                await upParamData("N",{show:false});
                await upParamData("theta",{show:false});
                await upParamData("h_w",{show:false});
                await upParamData("tray_material",{show:false});
                await upParamData("overflow_type",{show:false});
                await upParamData("packingType",{show:true});
                await upParamData("packing_total_height",{show:true});
            }
            else if(updates.value==="混合")
            {
                await upParamData("trayType",{show:true});
                await upParamData("N",{show:true});
                await upParamData("theta",{show:true});
                await upParamData("h_w",{show:true});
                await upParamData("tray_material",{show:true});
                await upParamData("overflow_type",{show:true});
                await upParamData("packingType",{show:true});
                await upParamData("packing_total_height",{show:true});
            }
        }
        //操作规范
        else if(engName==="operatingSpecifications" && selectedElementData.elementNodeType==="DistilStrict" && selectedElementData.elementSecondaryTypeName==="汽提塔")
        {
            if(updates.value==="塔底物流率")
            {
                await upParamData("operatingSpecificationsValue",{unitValue:"kmol/hr",unit:[{value:"kmol/hr",label:"kmol/hr"},{value:"kg/hr",label:"kg/hr"},{value:"cum/hr",label:"cum/hr"}]});
            }
            else if(updates.value==="再沸器负荷")
            {
                await upParamData("operatingSpecificationsValue",{unitValue:"Gcal/hr",unit:[{value:"Gcal/hr",label:"Gcal/hr"},{value:"kW",label:"kW"}]});
            }
        }
        // //流股属性(多流股换热器)
        // else if(engName==="streamAttribute" && selectedElementData.elementType==="node" && selectedElementData.elementNodeType==="MultiStreamExchanger" && selectedElementData.elementSecondaryTypeName==="换热器4")
        // {
        //     if(updates.value==="冷流股")
        //     {
        //         await upParamData("cold_out_T",{show:true});
        //         await upParamData("cold_out_P",{show:true});
        //         await upParamData("hot_out_T",{show:false});
        //         await upParamData("hot_out_P",{show:false});
        //     }
        //     else if(updates.value==="热流股")
        //     {
        //         await upParamData("cold_out_T",{show:false});
        //         await upParamData("cold_out_P",{show:false});
        //         await upParamData("hot_out_T",{show:true});
        //         await upParamData("hot_out_P",{show:true});
        //     }
        // }
        //流股属性(换热器)
        else if(engName==="streamAttribute" && selectedElementData.elementType==="node" && selectedElementData.elementNodeType==="ShellAndTubeExchanger")
        {
            if(updates.value==="冷流股")
            {
                await upParamData("cold_out_T",{show:true});
                await upParamData("cold_out_P",{show:true});
                await upParamData("hot_out_T",{show:false});
                await upParamData("hot_out_P",{show:false});
            }
            else if(updates.value==="热流股")
            {
                await upParamData("cold_out_T",{show:false});
                await upParamData("cold_out_P",{show:false});
                await upParamData("hot_out_T",{show:true});
                await upParamData("hot_out_P",{show:true});
            }
        }

        //单位
        if(updates.unitValue)
        {
            let nodeTypeParamsForm = await buildNodeTypeParamsForm();
            if(selectedElementData.elementType==="node")
            {
                let params = nodeTypeParamsForm[selectedElementData.elementSecondaryTypeName];
                if(params && params[engName])
                {
                    let unitField = params[engName].p_unitField;
                    if(unitField)
                    {
                        //寻找与其unitField相同的参数
                        let upUnitValueParams = Object.values(params).filter(item=>item["p_unitField"]===unitField && item["p_engName"]!==engName)
                        for(let i=0;i<upUnitValueParams.length;i++)
                        {
                            await upParamData(upUnitValueParams[i]["p_engName"],{unitValue:updates.unitValue});
                        }
                    }
                }
            }
            else if(selectedElementData.elementType==="link")
            {

            }
            else if(selectedElementData.elementType==="group")
            {

            }
        }
    }

    //粘贴组分
    const copyMaterialsData = async() =>
    {
        let copyMaterialsDatas = copyMaterialsDataTxt.split('\n');
        copyMaterialsDatas = copyMaterialsDatas.filter(item=>item!=="");
        let materialsClass = paramDatas.find(item=>item.className==="组分");
        if(materialsClass)
        {
            let materialsParams = materialsClass.params;

            for(let i=0;i<materialsParams.length;i++)
            {
                let engName = materialsParams[i].engName;
                if(copyMaterialsDatas[i])
                {
                    await upParamData(engName,{value:copyMaterialsDatas[i]});
                }
                else
                {
                    break;
                }
            }
        }
        else
        {
            return false;
        }

        return true;
    }

    //返回组件
    if(initializeVariable)
    {
        return (
            <div style={{display:"flex",flexDirection: "column",height:"100%"}}>
                
                {contextHolder}

                <div style={{display: "flex",flexDirection: "row",width:"100%",height:70,borderBottom:"1px solid #BBB",}}>
                    <div style={{display:"flex",alignItems:"center",minWidth:100,height:"100%",fontSize:18,fontWeight:"bold",color:"#5F5F5F",marginLeft:20}}>
                        {`${selectedElementData.elementName}：`}
                    </div>
                    {/* 关联流股和关联元件的下拉选择框 */}
                    {/* {selectedElementData.elementType==="node"||selectedElementData.elementType==="link"?(
                        <div style={{display:"flex",alignItems:"center",width:120}}>
                            {selectedElementData.elementType==="node"?(
                                <Select
                                    value={"关联流股"}
                                    onChange={async(val,e) => 
                                    {
                                        let element = relateElements.allLinks.find(item=>item.key===val);
                                        if(element)
                                        {
                                            let treeItem = await buildSelectedElementData(element.data);
                                            selectTreeItem(treeItem);
                                        }
                                    }}
                                    options={[
                                        {
                                            label:<span>进料流股</span>,
                                            title:"进料流股",
                                            options:relateElements.inLinks.map(({name,key})=>({label:name,value:key})),
                                        },
                                        {
                                            label:<span>出料流股</span>,
                                            title:"出料流股",
                                            options:relateElements.outLinks.map(({name,key})=>({label:name,value:key})),
                                        },
                                    ]}
                                    style={{flex:1}}
                                    getPopupContainer={(trigger) => trigger.parentNode}
                                />
                            ):null}
                            {selectedElementData.elementType==="link"?(
                                <Select
                                    value={"关联设备"}
                                    onChange={async(val,e) => 
                                    {
                                        let element = relateElements.allNodes.find(item=>item.key===val);
                                        if(element)
                                        {
                                            let treeItem = await buildSelectedElementData(element.data);
                                            selectTreeItem(treeItem);
                                        }
                                    }}
                                    options={[
                                        {
                                            label:<span>来源设备</span>,
                                            title:"来源设备",
                                            options:relateElements.fromNodes.map(({name,key})=>({label:name,value:key})),
                                        },
                                        {
                                            label:<span>流向设备</span>,
                                            title:"流向设备",
                                            options:relateElements.toNodes.map(({name,key})=>({label:name,value:key})),
                                        },
                                    ]}
                                    style={{flex:1}}
                                    getPopupContainer={(trigger) => trigger.parentNode}
                                />
                            ):null}
                        </div>
                    ):null} */}

                    {flowChartType==="task" && selectTaskData?(
                        <div style={{display:"flex",alignItems:"center"}}>
                            <button
                                onClick={async() => {runPartTask(selectedElementData.elementID);}}
                                style={{display:"flex",alignItems:"center",justifyContent: "center",width:120,height:30,border:"1px solid #1b6ae7",marginLeft:20,borderRadius:5,backgroundColor:"#FFF",color:"#1b6ae7",fontSize:12}}
                            >
                                <PlayCircleOutlined style={{ marginRight: 10 }} />
                                运行单元操作
                            </button>
                        </div>
                    ):null}
                </div>

                <div style={{display:"flex",flexDirection: "column",flex:1,backgroundColor:"transparent",marginLeft:20,marginRight:20,marginBottom:15,overflowY: "auto"}}>
                    
                    {/* 特殊处理:关联流股/关联设备 横向展开列出 */}
                    {(selectedElementData.elementType==="node"||selectedElementData.elementType==="link")?(
                        <div style={{display:"flex",flexDirection:"row",alignItems:"center",flexWrap:"wrap",minHeight:35,marginLeft:10,marginTop:10,fontSize:16,fontWeight:"bold",color:"#1b6ae7"}}>
                            {selectedElementData.elementType==="node"?(
                                <>
                                    <span style={{marginRight:8}}>关联流股：</span>
                                    <span style={{marginRight:6,fontSize:14,fontWeight:"normal",color:"#5F5F5F"}}>进料：</span>
                                    {relateElements.inLinks.length>0?(
                                        relateElements.inLinks.map(({name,key})=>(
                                            <span
                                                key={key}
                                                onClick={async() => { const element = relateElements.allLinks.find(item=>item.key===key); if(element){ const treeItem = await buildSelectedElementData(element.data); selectTreeItem(treeItem); } }}
                                                style={{cursor:"pointer",marginRight:6,marginBottom:4,fontSize:14,fontWeight:"normal",color:"#165DFF",background:"#e6f7ff",padding:"2px 8px",borderRadius:4}}
                                            >{name}</span>
                                        ))
                                    ):<span style={{marginRight:6,fontSize:14,fontWeight:"normal",color:"#999"}}>无</span>}
                                    <span style={{marginRight:6,fontSize:14,fontWeight:"normal",color:"#5F5F5F"}}>出料：</span>
                                    {relateElements.outLinks.length>0?(
                                        relateElements.outLinks.map(({name,key})=>(
                                            <span
                                                key={key}
                                                onClick={async() => { const element = relateElements.allLinks.find(item=>item.key===key); if(element){ const treeItem = await buildSelectedElementData(element.data); selectTreeItem(treeItem); } }}
                                                style={{cursor:"pointer",marginRight:6,marginBottom:4,fontSize:14,fontWeight:"normal",color:"#165DFF",background:"#e6f7ff",padding:"2px 8px",borderRadius:4}}
                                            >{name}</span>
                                        ))
                                    ):<span style={{fontSize:14,fontWeight:"normal",color:"#999"}}>无</span>}
                                </>
                            ):null}
                            {selectedElementData.elementType==="link"?(
                                <>
                                    <span style={{marginRight:8}}>关联设备：</span>
                                    <span style={{marginRight:6,fontSize:14,fontWeight:"normal",color:"#5F5F5F"}}>来源：</span>
                                    {relateElements.fromNodes.length>0?(
                                        relateElements.fromNodes.map(({name,key})=>(
                                            <span
                                                key={key}
                                                onClick={async() => { const element = relateElements.allNodes.find(item=>item.key===key); if(element){ const treeItem = await buildSelectedElementData(element.data); selectTreeItem(treeItem); } }}
                                                style={{cursor:"pointer",marginRight:6,marginBottom:4,fontSize:14,fontWeight:"normal",color:"#165DFF",background:"#e6f7ff",padding:"2px 8px",borderRadius:4}}
                                            >{name}</span>
                                        ))
                                    ):<span style={{marginRight:6,fontSize:14,fontWeight:"normal",color:"#999"}}>无</span>}
                                    <span style={{marginRight:6,fontSize:14,fontWeight:"normal",color:"#5F5F5F"}}>流向：</span>
                                    {relateElements.toNodes.length>0?(
                                        relateElements.toNodes.map(({name,key})=>(
                                            <span
                                                key={key}
                                                onClick={async() => { const element = relateElements.allNodes.find(item=>item.key===key); if(element){ const treeItem = await buildSelectedElementData(element.data); selectTreeItem(treeItem); } }}
                                                style={{cursor:"pointer",marginRight:6,marginBottom:4,fontSize:14,fontWeight:"normal",color:"#165DFF",background:"#e6f7ff",padding:"2px 8px",borderRadius:4}}
                                            >{name}</span>
                                        ))
                                    ):<span style={{fontSize:14,fontWeight:"normal",color:"#999"}}>无</span>}
                                </>
                            ):null}
                        </div>
                    ):null}

                    {/* 特殊处理:塔的内部类型单独展示 */}
                    {selectedElementData.elementNodeType==="DistilStrict"&& paramDatas && paramDatas.find(item=>item.className==="规定")?.params.find(item=>item.engName==="internalType")?(
                        <div style={{display:"flex",flexDirection:"row",alignItems:"center",flex:1,minHeight:35,maxHeight:35,marginLeft:10,marginTop:10,fontSize:16,fontWeight:"bold",color:"#1b6ae7"}}>
                            内部类型：
                            <Radio.Group
                                value={paramDatas.find(item=>item.className==="规定").params.find(item=>item.engName==="internalType").value}
                                onChange={(e) => upParamData("internalType",{value:e.target.value},true)}
                                style={{marginLeft:10,fontSize:14,color:"#5F5F5F",fontWeight:"normal"}}
                                options={paramDatas.find(item=>item.className==="规定").params.find(item=>item.engName==="internalType").enum.map(item => ({label:item.label,value:item.value }))}
                                disabled={flowChartType==="task"?false:true}
                            />
                        </div>
                    ):null}

                    <div style={{display: "flex",flexWrap:"wrap",alignContent: "flex-start",flex:1}}>
                        {paramDatas && paramDatas.map((classData) => (
                            <div key={classData.className} style={{display:classData.params.filter(item=>item.show===true).length>0?"flex":"none",flex: "0 0 calc(50% - 20px)",flexDirection:"column",border:"1px solid #BBB",borderRadius:5,padding:20,margin:10}}>
                                <div style={{display: "flex",flexDirection: "row",width:"100%",fontSize:16,fontWeight:"bold",color:"#1b6ae7",marginBottom:15}}>
                                    {classData.className}
                                    {/* 特殊处理:塔板效率添加按钮 */}
                                    {selectedElementData.elementNodeType==="DistilStrict" && classData.className==="塔板效率" && flowChartType==="task" ?(
                                        <button
                                            onClick={()=>{
                                                let newParams = structuredClone(classData.params.find(item=>item.engName==="eff"));
                                                if(newParams)
                                                {
                                                    let newChildren = newParams.children;
                                                    let newChild = 
                                                    {
                                                        "起始塔板":{value:null,desValue:null,show: true,engName:"start"},
                                                        "结束塔板":{value:null,desValue:null,show:true,engName:"end"},
                                                        "效率值": {value:null,desValue:null,show:true,engName:"value"},
                                                        "特殊效率": {value:[],desValue:[],show:false,engName:"spec",children:[]},
                                                    };
                                                    newChildren.push(newChild);
                                                    upParamData("eff",{children:newChildren})
                                                }
                                            }}
                                            title="添加"
                                            style={{marginLeft:"auto",marginRight:"15px",width:30,height:"100%",fontSize:16,color:"#1b6ae7",border:0,backgroundColor:"transparent"}}
                                        >
                                            <PlusCircleOutlined/>
                                        </button>
                                    ):null}
                                    {/* 特殊处理:组分添加按钮 */}
                                    {selectedElementData.elementType==="link" && classData.className==="组分" && flowChartType==="task" ?(
                                        <button
                                            onClick={()=>{setCopyMaterialVariable(true);}}
                                            title="粘贴组分"
                                            style={{marginLeft:"auto",marginRight:"15px",width:30,height:"100%",fontSize:16,color:"#1b6ae7",border:0,backgroundColor:"transparent"}}
                                        >
                                            <CopyOutlined/>
                                        </button>
                                    ):null}
                                </div>

                                <div style={{display: "flex",flexWrap:"wrap",alignContent:"flex-start",flex:1,rowGap: "15px",columnGap:"20px"}}>
                                    {classData.params && classData.params.map((paramData) => (
                                        <div key={paramData.name} style={{display:paramData.show?"flex":"none",flex:!(paramData.children)&&(paramData.name!=="描述")?"0 0 calc(50% - 10px)":1,height:!(paramData.children)&&(paramData.name!=="描述")?35:(paramData.name==="描述"?100:"100%"),flexDirection:"row",border:"0px solid #BBB",borderRadius:5}}>

                                                {/* 子项目 */}
                                                {paramData.children?(
                                                    <div style={{display:"flex",flexDirection:"row",flex:1}}>

                                                        {/* 进料流股位置 */}
                                                        {selectedElementData.elementNodeType==="DistilStrict" && paramData.engName==="Ns"?(
                                                            <div style={{display:"flex",flexWrap:"wrap",alignContent:"flex-start",rowGap:"15px",columnGap:"20px",flex:1}}>
                                                                {paramData.children.map((child) => (
                                                                    <div style={{display:"flex",flexDirection:"row",flex:"0 0 calc((100% - 40px)/3)",alignItems:"center"}}>
                                                                        <div style={{display:"flex",alignItems:"center",width:90,fontSize:14,color:"#5F5F5F"}}>{`${child["流股"].value}：`}</div>
                                                                        {flowChartType==="task"?(
                                                                            <Input
                                                                                value={child["进料位置"].value}
                                                                                onChange={(e) => {
                                                                                    let newChildren = paramData.children.map(item =>item === child ? {...item,"进料位置":{...item["进料位置"],value:e.target.value}}:item);
                                                                                    upParamData(paramData.engName,{children:newChildren})}}
                                                                                style={{flex:1}}
                                                                            />
                                                                        ):(
                                                                            <div style={{flex:1,display:"flex",alignItems:"center",height:35,fontSize:14,color:"#5F5F5F"}}>
                                                                                {child["进料位置"].value}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ):null}

                                                        {/* 出料流股位置 */}
                                                        {selectedElementData.elementNodeType==="DistilStrict" && paramData.engName==="No"?(
                                                            <div style={{display:"flex",flexWrap:"wrap",alignContent:"flex-start",rowGap:"15px",columnGap:"20px",flex:1}}>
                                                                {paramData.children.map((child) => (
                                                                    <div style={{display:"flex",flexDirection:"row",flex:"0 0 calc((100% - 40px)/3)",alignItems:"center"}}>
                                                                        <div style={{display:"flex",alignItems:"center",width:90,fontSize:14,color:"#5F5F5F"}}>{`${child["流股"].value}：`}</div>
                                                                        {flowChartType==="task"?(
                                                                            <Input
                                                                                value={child["出料位置"].value}
                                                                                onChange={(e) => {
                                                                                    let newChildren = paramData.children.map(item =>item === child ? {...item,"出料位置":{...item["出料位置"],value:e.target.value}}:item);
                                                                                    upParamData(paramData.engName,{children:newChildren})}}
                                                                                style={{flex:1}}
                                                                            />
                                                                        ):(
                                                                            <div style={{flex:1,display:"flex",alignItems:"center",height:35,fontSize:14,color:"#5F5F5F"}}>
                                                                                {child["出料位置"].value}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ):null}

                                                        {/* 塔板效率 */}
                                                        {selectedElementData.elementNodeType==="DistilStrict" && paramData.engName==="eff"?(
                                                            <div style={{display:"flex",flexDirection:"column",flex:1}}>
                                                                <div style={{display:"flex",flexDirection:"row",height:35}}>
                                                                    <div style={{display:"flex",justifyContent:"center",alignItems:"center",flex:1,fontSize:14,fontWeight:"bold",color:"#5F5F5F",backgroundColor:"#EEE",border:"1px solid #BBB"}}>起始塔板</div>
                                                                    <div style={{display:"flex",justifyContent:"center",alignItems:"center",flex:1,fontSize:14,fontWeight:"bold",color:"#5F5F5F",backgroundColor:"#EEE",borderWidth: "1px 1px 1px 0",borderStyle:"solid",borderColor:"#BBB"}}>结束塔板</div>
                                                                    <div style={{display:"flex",justifyContent:"center",alignItems:"center",flex:1,fontSize:14,fontWeight:"bold",color:"#5F5F5F",backgroundColor:"#EEE",borderWidth: "1px 1px 1px 0",borderStyle:"solid",borderColor:"#BBB"}}>效率值</div>
                                                                    <div style={{display:flowChartType==="task"?"flex":"none",justifyContent:"center",alignItems:"center",width:60,fontSize:14,fontWeight:"bold",color:"#5F5F5F",backgroundColor:"#EEE",borderWidth: "1px 1px 1px 0",borderStyle:"solid",borderColor:"#BBB"}}>操作</div>
                                                                </div>
                                                                {paramData.children.map((child) => (
                                                                    <div style={{display:"flex",flex:1,flexDirection:"row",minHeight:35,maxHeight:35}}>
                                                                        <Input
                                                                            value={child["起始塔板"].value}
                                                                            onChange={(e) => {
                                                                                let newChildren = paramData.children.map(item =>item === child ? {...item,"起始塔板":{...item["起始塔板"],value:e.target.value}}:item);
                                                                                upParamData(paramData.engName,{children:newChildren})}}
                                                                            style={{textAlign: "center",flex:1,height:35,fontSize:14,color:"#5F5F5F",backgroundColor:"#FFF",borderWidth: "0 1px 1px 1px",borderStyle:"solid",borderColor:"#BBB",borderRadius:0}}
                                                                            disabled={flowChartType==="task"?false:true}
                                                                        />
                                                                        <Input
                                                                            value={child["结束塔板"].value}
                                                                            onChange={(e) => {
                                                                                let newChildren = paramData.children.map(item =>item === child ? {...item,"结束塔板":{...item["结束塔板"],value:e.target.value}}:item);
                                                                                upParamData(paramData.engName,{children:newChildren})}}
                                                                            style={{textAlign: "center",flex:1,height:35,fontSize:14,color:"#5F5F5F",backgroundColor:"#FFF",borderWidth: "0 1px 1px 0",borderStyle:"solid",borderColor:"#BBB",borderRadius:0}}
                                                                            disabled={flowChartType==="task"?false:true}
                                                                        />
                                                                        <Input
                                                                            value={child["效率值"].value}
                                                                            onChange={(e) => {
                                                                                let newChildren = paramData.children.map(item =>item === child ? {...item,"效率值":{...item["效率值"],value:e.target.value}}:item);
                                                                                upParamData(paramData.engName,{children:newChildren})}}
                                                                            style={{textAlign: "center",flex:1,height:35,fontSize:14,color:"#5F5F5F",backgroundColor:"#FFF",borderWidth: "0 1px 1px 0",borderStyle:"solid",borderColor:"#BBB",borderRadius:0}}
                                                                            disabled={flowChartType==="task"?false:true}
                                                                        />
                                                                        {flowChartType==="task"?(
                                                                            <button
                                                                                onClick={()=>{
                                                                                    let newChildren = paramData.children.filter(item=>item!==child);
                                                                                    upParamData(paramData.engName,{children:newChildren})}}
                                                                                title="删除"
                                                                                style={{textAlign: "center",width:60,height:35,fontSize:14,color:"#FD7878",backgroundColor:"transparent",borderWidth: "0 1px 1px 0",borderStyle:"solid",borderColor:"#BBB",borderRadius:0}}
                                                                            >
                                                                                <DeleteOutlined/>
                                                                            </button>
                                                                        ):null}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ):null}

                                                        {/* 输出流量 */}
                                                        {selectedElementData.elementNodeType==="Splitter" && paramData.engName==="output_f"?(
                                                            <div style={{display:"flex",flexWrap:"wrap",alignContent:"flex-start",rowGap:"15px",columnGap:"20px",flex:1}}>
                                                                {paramData.children.map((child) => (
                                                                    <div style={{display:"flex",flexDirection:"row",flex:"0 0 calc((100% - 40px)/3)",alignItems:"center"}}>
                                                                        <div style={{display:"flex",alignItems:"center",width:90,fontSize:14,color:"#5F5F5F"}}>{`${child["流股"].value}：`}</div>
                                                                        {flowChartType==="task"?(
                                                                            <Input
                                                                                value={child["输出流量"].value}
                                                                                onChange={(e) => {
                                                                                    let newChildren = paramData.children.map(item =>item === child ? {...item,"输出流量":{...item["输出流量"],value:e.target.value}}:item);
                                                                                    upParamData(paramData.engName,{children:newChildren})}}
                                                                                style={{flex:1}}
                                                                            />
                                                                        ):(
                                                                            <div style={{flex:1,display:"flex",alignItems:"center",height:35,fontSize:14,color:"#5F5F5F"}}>
                                                                                {child["输出流量"].value}
                                                                            </div>
                                                                        )}

                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ):null}

                                                        {/* 冷流股出口温度 */}
                                                        {selectedElementData.elementNodeType==="MultiStreamExchanger" && selectedElementData.elementSecondaryTypeName==="换热器4" && paramData.engName==="cold_out_T"?(
                                                            <div style={{display:"flex",flexWrap:"wrap",alignContent:"flex-start",rowGap:"15px",columnGap:"20px",flex:1}}>
                                                                {paramData.children.map((child) => (
                                                                    <div style={{display:"flex",flexDirection:"row",flex:"0 0 calc((100% - 20px)/2)",alignItems:"center"}}>
                                                                        <div style={{display:"flex",alignItems:"center",width:90,fontSize:14,color:"#5F5F5F"}}>{`${child["流股"].value}：`}</div>
                                                                        {flowChartType==="task"?(
                                                                            <Input
                                                                                value={child["出口温度"].value}
                                                                                onChange={(e) => {
                                                                                    let newChildren = paramData.children.map(item =>item === child ? {...item,"出口温度":{...item["出口温度"],value:e.target.value}}:item);
                                                                                    upParamData(paramData.engName,{children:newChildren})}}
                                                                                style={{flex:1}}
                                                                            />
                                                                        ):(
                                                                            <div style={{flex:1,display:"flex",alignItems:"center",height:35,fontSize:14,color:"#5F5F5F"}}>
                                                                                {child["出口温度"].value}
                                                                            </div>
                                                                        )}
                                                                        {flowChartType==="task"?(
                                                                            <Select
                                                                                value={child["出口温度"].unit}
                                                                                onChange={(val,e) => {
                                                                                    let newChildren = paramData.children.map(item =>({...item,"出口温度":{...item["出口温度"],unit:val}}));
                                                                                    upParamData(paramData.engName,{children:newChildren});
                                                                                }}
                                                                                style={{width:80,marginLeft:10}}
                                                                                options={(unitMap["T"].map(item => ({value:item,label:item})))}
                                                                                getPopupContainer={(trigger) => trigger.parentNode}
                                                                            />
                                                                        ):(
                                                                            <div style={{width:80,marginLeft:10,display:"flex",alignItems:"center",height:35,fontSize:14,color:"#5F5F5F"}}>
                                                                                {child["出口温度"].unit}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ):null}

                                                        {/* 热流股出口温度 */}
                                                        {selectedElementData.elementNodeType==="MultiStreamExchanger" && selectedElementData.elementSecondaryTypeName==="换热器4" && paramData.engName==="hot_out_T"?(
                                                            <div style={{display:"flex",flexWrap:"wrap",alignContent:"flex-start",rowGap:"15px",columnGap:"20px",flex:1}}>
                                                                {paramData.children.map((child) => (
                                                                    <div style={{display:"flex",flexDirection:"row",flex:"0 0 calc((100% - 20px)/2)",alignItems:"center"}}>
                                                                        <div style={{display:"flex",alignItems:"center",width:90,fontSize:14,color:"#5F5F5F"}}>{`${child["流股"].value}：`}</div>
                                                                        {flowChartType==="task"?(
                                                                            <Input
                                                                                value={child["出口温度"].value}
                                                                                onChange={(e) => {
                                                                                    let newChildren = paramData.children.map(item =>item === child ? {...item,"出口温度":{...item["出口温度"],value:e.target.value}}:item);
                                                                                    upParamData(paramData.engName,{children:newChildren})}}
                                                                                style={{flex:1}}
                                                                            />
                                                                        ):(
                                                                            <div style={{flex:1,display:"flex",alignItems:"center",height:35,fontSize:14,color:"#5F5F5F"}}>
                                                                                {child["出口温度"].value}
                                                                            </div>
                                                                        )}
                                                                        {flowChartType==="task"?(
                                                                            <Select
                                                                                value={child["出口温度"].unit}
                                                                                onChange={(val,e) => {
                                                                                    let newChildren = paramData.children.map(item =>({...item,"出口温度":{...item["出口温度"],unit:val}}));
                                                                                    upParamData(paramData.engName,{children:newChildren});
                                                                                }}
                                                                                style={{width:80,marginLeft:10}}
                                                                                options={(unitMap["T"].map(item => ({value:item,label:item})))}
                                                                                getPopupContainer={(trigger) => trigger.parentNode}
                                                                            />
                                                                        ):(
                                                                            <div style={{width:80,marginLeft:10,display:"flex",alignItems:"center",height:35,fontSize:14,color:"#5F5F5F"}}>
                                                                                {child["出口温度"].unit}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ):null}

                                                        {/* 冷流股出口压力 */}
                                                        {selectedElementData.elementNodeType==="MultiStreamExchanger" && selectedElementData.elementSecondaryTypeName==="换热器4" && paramData.engName==="cold_out_P"?(
                                                            <div style={{display:"flex",flexWrap:"wrap",alignContent:"flex-start",rowGap:"15px",columnGap:"20px",flex:1}}>
                                                                {paramData.children.map((child) => (
                                                                    <div style={{display:"flex",flexDirection:"row",flex:"0 0 calc((100% - 20px)/2)",alignItems:"center"}}>
                                                                        <div style={{display:"flex",alignItems:"center",width:90,fontSize:14,color:"#5F5F5F"}}>{`${child["流股"].value}：`}</div>
                                                                        {flowChartType==="task"?(
                                                                            <Input
                                                                                value={child["出口压力"].value}
                                                                                onChange={(e) => {
                                                                                    let newChildren = paramData.children.map(item =>item === child ? {...item,"出口压力":{...item["出口压力"],value:e.target.value}}:item);
                                                                                    upParamData(paramData.engName,{children:newChildren})}}
                                                                                style={{flex:1}}
                                                                            />
                                                                        ):(
                                                                            <div style={{flex:1,display:"flex",alignItems:"center",height:35,fontSize:14,color:"#5F5F5F"}}>
                                                                                {child["出口压力"].value}
                                                                            </div>
                                                                        )}
                                                                        {flowChartType==="task"?(
                                                                            <Select
                                                                                value={child["出口压力"].unit}
                                                                                onChange={(val,e) => {
                                                                                    let newChildren = paramData.children.map(item =>({...item,"出口压力":{...item["出口压力"],unit:val}}));
                                                                                    upParamData(paramData.engName,{children:newChildren});
                                                                                }}
                                                                                style={{width:80,marginLeft:10}}
                                                                                options={(unitMap["P"].map(item => ({value:item,label:item})))}
                                                                                getPopupContainer={(trigger) => trigger.parentNode}
                                                                            />
                                                                        ):(
                                                                            <div style={{width:80,marginLeft:10,display:"flex",alignItems:"center",height:35,fontSize:14,color:"#5F5F5F"}}>
                                                                                {child["出口压力"].unit}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ):null}

                                                        {/* 热流股出口压力 */}
                                                        {selectedElementData.elementNodeType==="MultiStreamExchanger" && selectedElementData.elementSecondaryTypeName==="换热器4" && paramData.engName==="hot_out_P"?(
                                                            <div style={{display:"flex",flexWrap:"wrap",alignContent:"flex-start",rowGap:"15px",columnGap:"20px",flex:1}}>
                                                                {paramData.children.map((child) => (
                                                                    <div style={{display:"flex",flexDirection:"row",flex:"0 0 calc((100% - 20px)/2)",alignItems:"center"}}>
                                                                        <div style={{display:"flex",alignItems:"center",width:90,fontSize:14,color:"#5F5F5F"}}>{`${child["流股"].value}：`}</div>
                                                                        {flowChartType==="task"?(
                                                                            <Input
                                                                                value={child["出口压力"].value}
                                                                                onChange={(e) => {
                                                                                    let newChildren = paramData.children.map(item =>item === child ? {...item,"出口压力":{...item["出口压力"],value:e.target.value}}:item);
                                                                                    upParamData(paramData.engName,{children:newChildren})}}
                                                                                style={{flex:1}}
                                                                            />
                                                                        ):(
                                                                            <div style={{flex:1,display:"flex",alignItems:"center",height:35,fontSize:14,color:"#5F5F5F"}}>
                                                                                {child["出口压力"].value}
                                                                            </div>
                                                                        )}
                                                                        {flowChartType==="task"?(
                                                                            <Select
                                                                                value={child["出口压力"].unit}
                                                                                onChange={(val,e) => {
                                                                                    let newChildren = paramData.children.map(item =>({...item,"出口压力":{...item["出口压力"],unit:val}}));
                                                                                    upParamData(paramData.engName,{children:newChildren});
                                                                                }}
                                                                                style={{width:80,marginLeft:10}}
                                                                                options={(unitMap["P"].map(item => ({value:item,label:item})))}
                                                                                getPopupContainer={(trigger) => trigger.parentNode}
                                                                            />
                                                                        ):(
                                                                            <div style={{width:80,marginLeft:10,display:"flex",alignItems:"center",height:35,fontSize:14,color:"#5F5F5F"}}>
                                                                                {child["出口压力"].unit}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ):null}

                                                        {/* 出入流股关系 */}
                                                        {selectedElementData.elementNodeType==="MultiStreamExchanger" && selectedElementData.elementSecondaryTypeName==="换热器4" && paramData.engName==="streamRelationship"?(
                                                            <div style={{display:"flex",flexDirection:"column",flex:1}}>
                                                                <div style={{display:"flex",flexDirection:"row",height:35}}>
                                                                    <div style={{display:"flex",justifyContent:"center",alignItems:"center",flex:1,fontSize:14,fontWeight:"bold",color:"#5F5F5F",backgroundColor:"#EEE",border:"1px solid #BBB"}}>入口流股</div>
                                                                    <div style={{display:"flex",justifyContent:"center",alignItems:"center",flex:1,fontSize:14,fontWeight:"bold",color:"#5F5F5F",backgroundColor:"#EEE",borderWidth: "1px 1px 1px 0",borderStyle:"solid",borderColor:"#BBB"}}>出口流股</div>
                                                                </div>
                                                                {paramData.children.map((child) => (
                                                                    <div style={{display:"flex",flex:1,flexDirection:"row",minHeight:35,maxHeight:35}}>
                                                                        <div style={{display:"flex",justifyContent:"center",alignItems:"center",flex:1,fontSize:14,color:"#5F5F5F",borderWidth: "0 1px 1px 1px",borderStyle:"solid",borderColor:"#BBB"}}>{child["入口流股"].value}</div>
                                                                        <div style={{display:"flex",justifyContent:"center",alignItems:"center",flex:1,fontSize:14,color:"#5F5F5F",borderWidth: "0 1px 1px 0",borderStyle:"solid",borderColor:"#BBB"}}>{child["出口流股"].value}</div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ):null}

                                                    </div>
                                                ):(null)}

                                                {/* 描述 */}
                                                {paramData.name==="描述"?(
                                                    <div style={{display:"flex",flexDirection:"row",flex:1}}>
                                                        <div style={{ flex: 1,minHeight:"100%",maxHeight:"100%",display:"flex",fontSize:14,color:"#5F5F5F"}}>
                                                            {paramData.value}
                                                        </div>
                                                        {/* <Input.TextArea
                                                            value={paramData.value}
                                                            onChange={(e) => {upParamData(paramData.engName,{value:e.target.value})}}
                                                            autoSize={{maxRows:undefined}}
                                                            style={{ flex: 1,minHeight:"100%",maxHeight:"100%"}}
                                                        /> */}
                                                    </div>
                                                ):(null)}

                                                {/* 普通参数 */}
                                                {!(paramData.children)&&(paramData.name!=="描述")?(
                                                    <div style={{display:"flex",flexDirection:"row",flex:1}}>
                                                        <div style={{display:"flex",alignItems:"center",width:paramData.class!=="组分"?100:150,fontSize:14,color:"#5F5F5F"}}>{paramData.class!=="组分"?`${paramData.name}：`:`${rebuildFormula(paramData.engName)}（${paramData.name}）：`}</div>
                                                        <div style={{display:"flex",flex:1,flexDirection:"row",marginLeft:10}}>
                                                            {paramData.enum?(
                                                                <div style={{display:"flex",flexDirection:"row",flex:1}}>
                                                                    {flowChartType==="task"?(
                                                                        <Select
                                                                            value={paramData.value}
                                                                            onChange={(val,e) => {
                                                                                //需要特殊处理的参数
                                                                                if(paramData.engName==="internalType"||paramData.engName==="operatingSpecifications"||paramData.engName==="streamAttribute")
                                                                                {
                                                                                    upParamData(paramData.engName,{value:val},true);
                                                                                }
                                                                                else
                                                                                {
                                                                                    upParamData(paramData.engName,{value:val});
                                                                                }
                                                                            }}
                                                                            style={{flex:1}}
                                                                            options={paramData.enum.map((opt:any) => ({label: opt.label,value: opt.value}))}
                                                                            getPopupContainer={(trigger) => trigger.parentNode}
                                                                        /> 
                                                                    ):(
                                                                        <div style={{flex:1,display:"flex",alignItems:"center",height:35,fontSize:14,color:"#5F5F5F"}}>
                                                                            {(paramData.enum.map((opt:any) => ({label: opt.label,value: opt.value}))).find(item=>item.value===paramData.value)?.label}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ):(
                                                                <div style={{display:"flex",flexDirection:"row",flex:1}}>
                                                                    {flowChartType==="task"?(
                                                                        <Input
                                                                            value={paramData.value}
                                                                            onChange={(e) => {
                                                                                //需要特殊处理的参数
                                                                                if(paramData.engName==="internalType"||paramData.engName==="operatingSpecifications"||paramData.engName==="streamAttribute")
                                                                                {
                                                                                    upParamData(paramData.engName,{value:e.target.value},true);
                                                                                }
                                                                                else
                                                                                {
                                                                                    upParamData(paramData.engName,{value:e.target.value});
                                                                                }
                                                                            }}
                                                                            style={{flex:1}}
                                                                        />
                                                                    ):(
                                                                        <div style={{flex:1,display:"flex",alignItems:"center",height:35,fontSize:14,color:"#5F5F5F"}}>
                                                                            {paramData.value}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {paramData.unit?(
                                                                <div style={{width:80,marginLeft:10}}>
                                                                    {flowChartType==="task"?(
                                                                        <Select
                                                                            value={paramData.unitValue}
                                                                            onChange={(val,e) => {
                                                                                //需要特殊处理的参数
                                                                                if(paramData.unitField)
                                                                                {
                                                                                    upParamData(paramData.engName,{unitValue:val},true);
                                                                                }
                                                                                else
                                                                                {
                                                                                    upParamData(paramData.engName,{unitValue:val});
                                                                                }
                                                                            }}
                                                                            style={{display:"flex",flex:1}}
                                                                            options={paramData.unit.map((opt:any) => ({label: opt.label,value: opt.value}))}
                                                                            getPopupContainer={(trigger) => trigger.parentNode}
                                                                        />
                                                                    ):(
                                                                        <div style={{flex:1,display:"flex",alignItems:"center",height:35,fontSize:14,color:"#5F5F5F"}}>
                                                                            {paramData.unitValue}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                
                                                            ):null}
                                                        </div>
                                                    </div>
                                                ):(null)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div style={{display:"flex",flexDirection:"row",height:35,marginLeft:20,marginRight:20,marginBottom:60}}>
                    <button
                        onClick={async() => 
                            {
                                if(flowChartType==="task")
                                {
                                    let res = await saveLoaclParams();
                                    if(res)
                                    {
                                        closeParamsDrawer();
                                    }
                                }
                                else
                                {
                                    closeParamsDrawer();
                                }
                            }}
                        style={{display:"flex",alignItems:"center",justifyContent: "center",width:90,border:0,borderRadius:5,backgroundColor:"#1b6ae7",color:"#FFF",fontSize:12}}
                    >
                        确认
                    </button>
                    
                    <button
                        onClick={() => {closeParamsDrawer();}}
                        style={{display:flowChartType==="task"?"flex":"none",alignItems:"center",justifyContent: "center",width:90,marginLeft:15,border:"1px solid #1b6ae7",borderRadius:5,backgroundColor:"#FFF",color:"#1b6ae7",fontSize:12}}
                    >
                        取消
                    </button>
                </div>

                <Modal
                    zIndex={2000}
                    closable={false}
                    centered={true}
                    footer={null}
                    width={500}
                    className={"modal"}
                    open={copyMaterialVariable}
                    onCancel={()=>{setCopyMaterialVariable(false);setCopyMaterialsDataTxt("");}}
                >
                    <div style={{marginBottom:20,fontSize:20,fontWeight:"bold",color:"#1b6ae7"}}>粘贴组分摩尔分率</div>
                    <div>
                    <Input.TextArea
                        autoSize={{maxRows:undefined}}
                        value={copyMaterialsDataTxt}
                        onChange={(e) => {setCopyMaterialsDataTxt(e.target.value);}}
                        style={{ flex: 1,minHeight:400,maxHeight:400}}
                    />
                    </div>
                    <div style={{height:32}}>
                    <button
                        onClick={()=>{{setCopyMaterialVariable(false);setCopyMaterialsDataTxt("");}}}
                        style={{position:"absolute",right:25,bottom:10,width:88,height:32,border:0,borderRadius:5,background:"#FF4D4F",color:"#FFF",fontSize:14}}
                    >取消</button>
                    <button
                        onClick={async()=>{
                            let res = await copyMaterialsData();
                            if(res)
                            {
                                setCopyMaterialVariable(false);
                                setCopyMaterialsDataTxt("");
                            }
                        }}
                        style={{position:"absolute",right:138,bottom:10,width:88,height:32,border:0,borderRadius:5,background:"#1b6ae7",color:"#FFF",fontSize:14}}
                    >确认</button>
                    </div>
                </Modal>

            </div>
        );
    }
};

export default ParamsEdit; // 导出组件，供其他模块使用
